import React, { useEffect, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Edit, Trash, Filter, User, UserPlus, MailOut, Send, Upload, Download } from 'iconoir-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 7 * 86400) return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function GuestsPage() {
  const { 
    currentWedding, guests, events,
    fetchGuests, createGuest, updateGuest, deleteGuest, fetchEvents
  } = useWedding();
  
  const [showModal, setShowModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [filterRSVP, setFilterRSVP] = useState('all');
  const [filterSide, setFilterSide] = useState('all');
  const [sendingInvite, setSendingInvite] = useState(null); // guest id while sending
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    side: 'both',
    rsvp_status: 'pending',
    plus_ones: '0',
    dietary_restrictions: '',
    events_invited: [],
    notes: ''
  });

  useEffect(() => {
    if (currentWedding) {
      fetchGuests(currentWedding.id);
      fetchEvents(currentWedding.id);
    }
  }, [currentWedding, fetchGuests, fetchEvents]);

  const handleOpenModal = (guest = null) => {
    if (guest) {
      setEditingGuest(guest);
      setFormData({
        name: guest.name,
        email: guest.email || '',
        phone: guest.phone || '',
        side: guest.side || 'both',
        rsvp_status: guest.rsvp_status || 'pending',
        plus_ones: guest.plus_ones?.toString() || '0',
        dietary_restrictions: guest.dietary_restrictions || '',
        events_invited: guest.events_invited || [],
        notes: guest.notes || ''
      });
    } else {
      setEditingGuest(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        side: 'both',
        rsvp_status: 'pending',
        plus_ones: '0',
        dietary_restrictions: '',
        events_invited: [],
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const guestData = {
      wedding_id: currentWedding.id,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      side: formData.side,
      rsvp_status: formData.rsvp_status,
      plus_ones: parseInt(formData.plus_ones) || 0,
      dietary_restrictions: formData.dietary_restrictions,
      events_invited: formData.events_invited,
      notes: formData.notes
    };

    if (editingGuest) {
      await updateGuest(editingGuest.id, guestData);
    } else {
      await createGuest(guestData);
    }
    setShowModal(false);
    fetchGuests(currentWedding.id);
  };

  const handleDelete = async (guestId) => {
    if (window.confirm('Are you sure you want to delete this guest?')) {
      await deleteGuest(guestId);
    }
  };

  const handleRSVPChange = async (guest, newStatus) => {
    await updateGuest(guest.id, { rsvp_status: newStatus });
  };

  const filteredGuests = guests.filter(guest => {
    if (filterRSVP !== 'all' && guest.rsvp_status !== filterRSVP) return false;
    if (filterSide !== 'all' && guest.side !== filterSide) return false;
    return true;
  });

  const totalGuests = guests.length;
  const confirmedGuests = guests.filter(g => g.rsvp_status === 'confirmed').length;
  const totalAttendees = guests
    .filter(g => g.rsvp_status === 'confirmed')
    .reduce((sum, g) => sum + 1 + (g.plus_ones || 0), 0);

  const getRSVPBadge = (status) => {
    const styles = {
      confirmed: 'bg-moss/10 text-moss border-moss/30',
      declined: 'bg-terracotta/10 text-terracotta border-terracotta/30',
      pending: 'bg-ochre/10 text-ochre border-ochre/30'
    };
    return styles[status] || styles.pending;
  };

  const getSideBadge = (side) => {
    const labels = { bride: "Bride's", groom: "Groom's", both: "Both" };
    return labels[side] || side;
  };

  const uninvitedWithEmailCount = guests.filter(
    (g) => g.email && !g.last_invited_at,
  ).length;

  const handleSendInvite = async (guest) => {
    if (!guest.email) {
      toast.error("Add an email to this guest first.");
      return;
    }
    setSendingInvite(guest.id);
    try {
      await axios.post(
        `${API}/guests/${guest.id}/send-invite`,
        {},
        { withCredentials: true },
      );
      toast.success(`RSVP invite sent to ${guest.name}`);
      // Refetch so the "Invited just now" pill appears.
      await fetchGuests(currentWedding.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the invite.");
    } finally {
      setSendingInvite(null);
    }
  };

  const handleBulkSendInvites = async () => {
    setBulkSending(true);
    try {
      const { data } = await axios.post(
        `${API}/guests/send-bulk-invites`,
        { wedding_id: currentWedding.id, custom_note: bulkNote.trim() },
        { withCredentials: true },
      );
      const sent = data?.sent || 0;
      const skipped = data?.skipped_no_email || 0;
      const failed = data?.failed || 0;
      const parts = [];
      if (sent) parts.push(`${sent} invite${sent === 1 ? '' : 's'} sent`);
      if (skipped) parts.push(`${skipped} skipped (no email)`);
      if (failed) parts.push(`${failed} failed`);
      toast[failed && !sent ? 'error' : 'success'](
        parts.join(' · ') || 'No invites to send.',
      );
      await fetchGuests(currentWedding.id);
      setShowBulkInvite(false);
      setBulkNote('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk invite failed.');
    } finally {
      setBulkSending(false);
    }
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    setCsvFileName(file.name);
    setImportSummary(null);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(String(ev.target?.result || ''));
    reader.onerror = () => toast.error("Couldn't read that file. Make sure it's a .csv file.");
    reader.readAsText(file);
  };

  const handleResetImport = () => {
    setCsvText('');
    setCsvFileName('');
    setImportSummary(null);
  };

  const handleImportCsv = async () => {
    if (!csvText.trim()) {
      toast.error('Choose a CSV file first.');
      return;
    }
    setImporting(true);
    setImportSummary(null);
    try {
      const { data } = await axios.post(
        `${API}/guests/bulk-import`,
        { wedding_id: currentWedding.id, csv_text: csvText },
        { withCredentials: true },
      );
      setImportSummary(data);
      const imported = data?.imported || 0;
      const skipped = data?.skipped_count || 0;
      if (imported > 0) {
        toast.success(
          `${imported} guest${imported === 1 ? '' : 's'} imported${skipped ? ` · ${skipped} skipped` : ''}`,
        );
        await fetchGuests(currentWedding.id);
      } else {
        toast.error('No guests imported. Check your CSV format.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'CSV import failed.');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    const header = 'name,email,phone,events_invited,plus_ones,dietary_restrictions,side,rsvp_status,notes';
    const sample = [
      'Priya Patel,priya@example.com,+1 555 1234,"Mehndi, Reception",1,Vegetarian,bride,confirmed,Cousin of the bride',
      'Arjun Khan,arjun@example.com,,Reception,0,,groom,pending,',
    ].join('\n');
    const csv = `${header}\n${sample}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shaadi-planner-guests-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!currentWedding) {
    return (
      <Layout>
        <div className="text-center py-12 text-muted">
          Please select or create a wedding first.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="guests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
              Guest List
            </h1>
            <p className="text-muted font-light mt-2">
              Manage your wedding guests and RSVPs
            </p>
          </div>
          <div className="flex items-center gap-2">
            {uninvitedWithEmailCount > 0 && (
              <Button
                onClick={() => setShowBulkInvite(true)}
                className="btn-secondary flex items-center gap-2"
                data-testid="bulk-invite-btn"
              >
                <Send className="w-4 h-4" />
                Send {uninvitedWithEmailCount} invite{uninvitedWithEmailCount === 1 ? '' : 's'}
              </Button>
            )}
            <Button
              onClick={() => setShowImport(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </Button>
            <Button 
              onClick={() => handleOpenModal()}
              className="btn-primary flex items-center gap-2"
              data-testid="add-guest-btn"
            >
              <Plus className="w-4 h-4" />
              Add Guest
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="card-surface p-6">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-muted" />
              <div>
                <p className="text-sm text-muted uppercase tracking-wider">Total Invited</p>
                <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="total-invited">
                  {totalGuests}
                </p>
              </div>
            </div>
          </Card>
          <Card className="card-surface p-6">
            <div className="flex items-center gap-3">
              <UserPlus className="w-5 h-5 text-moss" />
              <div>
                <p className="text-sm text-muted uppercase tracking-wider">Confirmed</p>
                <p className="font-mono text-2xl text-moss tracking-tight" data-testid="confirmed-guests">
                  {confirmedGuests}
                </p>
              </div>
            </div>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Total Attendees</p>
            <p className="font-mono text-2xl text-charcoal tracking-tight" data-testid="total-attendees">
              {totalAttendees}
            </p>
            <p className="text-xs text-muted">Including plus ones</p>
          </Card>
          <Card className="card-surface p-6">
            <p className="text-sm text-muted uppercase tracking-wider mb-1">Pending</p>
            <p className="font-mono text-2xl text-ochre tracking-tight" data-testid="pending-rsvp">
              {guests.filter(g => g.rsvp_status === 'pending').length}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-surface p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-4 h-4 text-muted" />
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted">RSVP:</Label>
              <Select value={filterRSVP} onValueChange={setFilterRSVP}>
                <SelectTrigger className="w-32 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted">Side:</Label>
              <Select value={filterSide} onValueChange={setFilterSide}>
                <SelectTrigger className="w-32 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="bride">Bride's</SelectItem>
                  <SelectItem value="groom">Groom's</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Guests Table */}
        <Card className="card-surface overflow-hidden">
          {filteredGuests.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-muted mb-4">No guests found</p>
              <Button 
                onClick={() => handleOpenModal()}
                className="btn-primary"
                data-testid="create-first-guest-btn"
              >
                Add Your First Guest
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border">
                    <TableHead className="text-muted uppercase text-xs tracking-wider">Name</TableHead>
                    <TableHead className="text-muted uppercase text-xs tracking-wider">Side</TableHead>
                    <TableHead className="text-muted uppercase text-xs tracking-wider">RSVP</TableHead>
                    <TableHead className="text-muted uppercase text-xs tracking-wider text-center">+1s</TableHead>
                    <TableHead className="text-muted uppercase text-xs tracking-wider">Contact</TableHead>
                    <TableHead className="text-muted uppercase text-xs tracking-wider">Invite</TableHead>
                    <TableHead className="text-muted uppercase text-xs tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGuests.map((guest) => (
                    <TableRow key={guest.id} className="border-b border-border" data-testid={`guest-row-${guest.id}`}>
                      <TableCell className="font-sans text-charcoal">{guest.name}</TableCell>
                      <TableCell className="text-muted">{getSideBadge(guest.side)}</TableCell>
                      <TableCell>
                        <Select 
                          value={guest.rsvp_status} 
                          onValueChange={(value) => handleRSVPChange(guest, value)}
                        >
                          <SelectTrigger className="w-28 h-8 text-xs border-0 p-0">
                            <Badge className={`${getRSVPBadge(guest.rsvp_status)} border capitalize`}>
                              {guest.rsvp_status}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center font-mono text-charcoal">
                        {guest.plus_ones || 0}
                      </TableCell>
                      <TableCell className="text-muted text-sm">
                        {guest.email || guest.phone || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {guest.last_invited_at ? (
                          <Badge
                            className="bg-moss/10 text-moss border-moss/30 border text-[11px]"
                            data-testid={`invited-badge-${guest.id}`}
                          >
                            <MailOut className="w-3 h-3 mr-1" strokeWidth={1.5} />
                            Invited {relativeTime(guest.last_invited_at)}
                          </Badge>
                        ) : guest.email ? (
                          <button
                            onClick={() => handleSendInvite(guest)}
                            disabled={sendingInvite === guest.id}
                            className="text-xs text-terracotta hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`send-invite-btn-${guest.id}`}
                          >
                            <Send className="w-3 h-3" strokeWidth={1.5} />
                            {sendingInvite === guest.id ? 'Sending...' : 'Send invite'}
                          </button>
                        ) : (
                          <span className="text-xs text-muted">No email</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(guest)}
                            className="p-2 text-muted hover:text-charcoal transition-colors"
                            data-testid={`edit-guest-${guest.id}`}
                          >
                            <Edit className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          <button
                            onClick={() => handleDelete(guest.id)}
                            className="p-2 text-muted hover:text-terracotta transition-colors"
                            data-testid={`delete-guest-${guest.id}`}
                          >
                            <Trash className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingGuest ? 'Edit Guest' : 'Add New Guest'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field h-12"
                  placeholder="Guest name"
                  required
                  data-testid="guest-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field h-12"
                    placeholder="email@example.com"
                    data-testid="guest-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field h-12"
                    placeholder="Phone number"
                    data-testid="guest-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Side</Label>
                  <Select 
                    value={formData.side} 
                    onValueChange={(value) => setFormData({ ...formData, side: value })}
                  >
                    <SelectTrigger className="input-field h-12" data-testid="guest-side-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bride">Bride's Side</SelectItem>
                      <SelectItem value="groom">Groom's Side</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">RSVP Status</Label>
                  <Select 
                    value={formData.rsvp_status} 
                    onValueChange={(value) => setFormData({ ...formData, rsvp_status: value })}
                  >
                    <SelectTrigger className="input-field h-12" data-testid="guest-rsvp-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Plus Ones</Label>
                <Input
                  type="number"
                  value={formData.plus_ones}
                  onChange={(e) => setFormData({ ...formData, plus_ones: e.target.value })}
                  className="input-field h-12 font-mono"
                  min="0"
                  data-testid="guest-plus-ones-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Dietary Restrictions</Label>
                <Input
                  value={formData.dietary_restrictions}
                  onChange={(e) => setFormData({ ...formData, dietary_restrictions: e.target.value })}
                  className="input-field h-12"
                  placeholder="Any dietary requirements"
                  data-testid="guest-dietary-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field h-12"
                  placeholder="Additional notes"
                  data-testid="guest-notes-input"
                />
              </div>

              <div className="space-y-2" data-testid="guest-events-invited-section">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">
                  Events Invited
                </Label>
                {events.length === 0 ? (
                  <p className="text-xs text-muted font-light">
                    Add events first, then mark which ones this guest is invited to.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {events.map((ev) => {
                      const checked = formData.events_invited.includes(ev.name);
                      const toggle = () => {
                        setFormData((prev) => ({
                          ...prev,
                          events_invited: checked
                            ? prev.events_invited.filter((n) => n !== ev.name)
                            : [...prev.events_invited, ev.name],
                        }));
                      };
                      return (
                        <button
                          type="button"
                          key={ev.id}
                          onClick={toggle}
                          data-testid={`guest-event-chip-${ev.id}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-light tracking-wide border transition-colors ${
                            checked
                              ? 'bg-charcoal text-white border-charcoal'
                              : 'bg-white text-charcoal border-stone hover:border-charcoal'
                          }`}
                        >
                          {ev.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary flex-1"
                  data-testid="save-guest-btn"
                >
                  {editingGuest ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk RSVP-invite dialog */}
        <Dialog open={showBulkInvite} onOpenChange={setShowBulkInvite}>
          <DialogContent className="bg-white max-w-lg" data-testid="bulk-invite-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                Send RSVP invites
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted">
                We'll email an RSVP link to{' '}
                <span className="text-charcoal font-medium">{uninvitedWithEmailCount}</span>{' '}
                guest{uninvitedWithEmailCount === 1 ? '' : 's'} who have an email and haven't been invited yet.
              </p>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted">Personal note (optional)</Label>
                <Textarea
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                  placeholder="We can't wait to celebrate with you..."
                  maxLength={500}
                  rows={3}
                  className="input-field mt-1"
                  data-testid="bulk-invite-note"
                />
                <p className="text-[11px] text-muted mt-1">{bulkNote.length}/500 — appears in italics inside the invite.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => { setShowBulkInvite(false); setBulkNote(''); }}
                  className="btn-secondary"
                  data-testid="bulk-invite-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkSendInvites}
                  disabled={bulkSending || uninvitedWithEmailCount === 0}
                  className="btn-primary flex items-center gap-2"
                  data-testid="bulk-invite-confirm"
                >
                  <Send className="w-4 h-4" />
                  {bulkSending ? 'Sending...' : `Send ${uninvitedWithEmailCount} invite${uninvitedWithEmailCount === 1 ? '' : 's'}`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* CSV bulk-import dialog */}
        <Dialog
          open={showImport}
          onOpenChange={(open) => {
            setShowImport(open);
            if (!open) handleResetImport();
          }}
        >
          <DialogContent className="bg-white max-w-lg" data-testid="csv-import-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Import guests from CSV</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted leading-relaxed">
                Upload a CSV with one row per guest. Only <span className="text-charcoal font-medium">name</span> is required —
                everything else is optional. Recognised columns: <code className="text-[11px] bg-page px-1 py-0.5 rounded">name, email, phone, events_invited, plus_ones, dietary_restrictions, side, rsvp_status, notes</code>.
              </p>

              <button
                type="button"
                onClick={handleDownloadCsvTemplate}
                className="text-xs text-terracotta hover:underline inline-flex items-center gap-1"
                data-testid="download-csv-template-btn"
              >
                <Download className="w-3 h-3" strokeWidth={1.5} />
                Download CSV template
              </button>

              <label className="block">
                <span className="block text-xs uppercase tracking-wider text-muted mb-2">CSV file</span>
                <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-terracotta/40 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => handleCsvFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  <Upload className="w-6 h-6 mx-auto text-muted mb-2" strokeWidth={1.5} />
                  <p className="text-sm text-charcoal">
                    {csvFileName || 'Click to choose a .csv file'}
                  </p>
                  {!csvFileName && (
                    <p className="text-xs text-muted mt-1">First row should be column headers</p>
                  )}
                </div>
              </label>

              {importSummary && (
                <div
                  className="rounded-sm border border-border bg-page p-3 text-xs"
                  data-testid="csv-import-summary"
                >
                  <p className="text-charcoal">
                    <span className="font-medium text-moss">{importSummary.imported || 0} imported</span>
                    {(importSummary.skipped_count || 0) > 0 && (
                      <>
                        {' · '}
                        <span className="font-medium text-terracotta">
                          {importSummary.skipped_count} skipped
                        </span>
                      </>
                    )}
                  </p>
                  {Array.isArray(importSummary.skipped) && importSummary.skipped.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-muted">
                      {importSummary.skipped.slice(0, 5).map((s, i) => (
                        <li key={i}>Row {s.row}: {s.reason}</li>
                      ))}
                      {importSummary.skipped.length > 5 && (
                        <li>…and {importSummary.skipped.length - 5} more</li>
                      )}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  onClick={() => { setShowImport(false); handleResetImport(); }}
                  className="btn-secondary"
                  data-testid="csv-import-cancel"
                >
                  {importSummary?.imported ? 'Done' : 'Cancel'}
                </Button>
                <Button
                  onClick={handleImportCsv}
                  disabled={importing || !csvText}
                  className="btn-primary flex items-center gap-2"
                  data-testid="csv-import-confirm"
                >
                  <Upload className="w-4 h-4" />
                  {importing ? 'Importing...' : 'Import guests'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
